import React, { memo } from 'react'
import { Grid, Stack } from '@mui/material'
import { faCoins, faPlug } from '@fortawesome/pro-light-svg-icons'

import PageContainer from '../shared/PageContainer/PageContainer'
import DisplayAlerts from '../setup/common/DisplayAlerts'
import BaseCard from '../shared/BaseCard/BaseCard'
import PanelHeading from '../shared/PanelHeading/PanelHeading'
import TabContent from '../shared/TabContent/TabContent'
import CustomButton from '../shared/buttons/CustomButton'
import LoveBrandCard from '../shared/LoveBrandCard/LoveBrandCard'
import { EXTENSIONS_TAB_TYPE } from '../../constants/extensions'
import TabDescription from '../shared/TabContent/TabDescription'
import {
  buildLoveBrandCardProps,
  buildThirdPartyToolTypes,
  fromExtensionsTypeToTabId,
  sortOptionsByTitleASC
} from '../../util/extensions/extensions.helpers'
import CardGrid from '../shared/containers/CardGrid'
import {
  EARN_OPTIONS_SURVEY_ICONS,
  LAD_SURVEY_IDS
} from '../../constants/survey'
import Dropdown from '../shared/inputs/Dropdown'
import { EXTENSIONS_PROFILE_DISPATCH } from '../../store/reducers/extensions/extensions'
import DefaultText from '../shared/text/DefaultText'
import CardListGrid from '../shared/containers/CardListGrid'

const Extensions = ({
  t,
  selectedTab,
  onChangeTab,
  isDashboardSetupReady,
  tokenProfile,
  billingProfile,
  onClickSuggestFavorite,
  onClickBookCall,
  surveyProfile: { surveys, userSurveys },
  onLikeUnlikeBrand,
  changeToolsFilterState,
  extensionsProfile: {
    toolsFilterType,
    filteredThirdPartyTools,
    userSurveysLikesCount
  }
}) => {
  const title = t('extensions.extensions')
  const thirdPartyToolsSurvey =
    surveys[LAD_SURVEY_IDS.THIRD_PARTY_TOOLS]?.questions?.en
  const earnOptionsSurvey = surveys[LAD_SURVEY_IDS.EARN_OPTIONS]?.questions?.en
  const buildContent = () => {
    switch (selectedTab) {
      case EXTENSIONS_TAB_TYPE.THIRD_PARTY_TOOLS:
        return (
          <>
            <DefaultText
              label={thirdPartyToolsSurvey?.description}
              component='div'
              sx={{ mb: '5px' }}
            />
            <Grid container sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Grid item xs={12} md={6}>
                <Dropdown
                  id={EXTENSIONS_PROFILE_DISPATCH.TOOLS_FILTER_TYPE}
                  label={t('common.type')}
                  options={buildThirdPartyToolTypes(t)}
                  onChange={({ target }) =>
                    changeToolsFilterState(target.value)
                  }
                  value={toolsFilterType}
                  size='small'
                  sx={{ width: 224 }}
                />
              </Grid>
              <Grid item xs={12} md={6} display='flex' justifyContent='end'>
                <CustomButton
                  variant='outlined'
                  fullWidth={false}
                  label={t('extensions.suggest-favorite-tool')}
                  onClick={onClickSuggestFavorite}
                />
              </Grid>
            </Grid>
          </>
        )

      case EXTENSIONS_TAB_TYPE.EARN_OPTIONS:
        return (
          <TabDescription
            t={t}
            description={earnOptionsSurvey?.description}
            actions={
              <Stack
                spacing={1}
                direction={{
                  xs: 'column',
                  sm: 'row',
                  justifyContent: 'end'
                }}
                display={{ md: 'grid' }}
                gridAutoFlow={{ md: 'column' }}
              >
                <CustomButton
                  fullWidth={false}
                  label={t('extensions.book-a-call')}
                  onClick={onClickBookCall}
                />
                <CustomButton
                  variant='outlined'
                  fullWidth={false}
                  label={t('extensions.suggest-favorite-option')}
                  onClick={onClickSuggestFavorite}
                />
              </Stack>
            }
          />
        )
      default:
        return null
    }
  }

  const buildPartnersList = () => {
    switch (selectedTab) {
      case EXTENSIONS_TAB_TYPE.THIRD_PARTY_TOOLS:
        return sortOptionsByTitleASC(filteredThirdPartyTools).map(
          ({ id, ...question }, index) => (
            <LoveBrandCard
              t={t}
              key={id + index}
              {...buildLoveBrandCardProps(
                LAD_SURVEY_IDS.THIRD_PARTY_TOOLS,
                {
                  userSurveys,
                  userSurveysLikesCount,
                  id
                },
                onLikeUnlikeBrand
              )}
              {...question}
            />
          )
        )

      case EXTENSIONS_TAB_TYPE.EARN_OPTIONS:
        return earnOptionsSurvey?.questions?.map(
          ({ id, ...question }, index) => (
            <LoveBrandCard
              t={t}
              key={id + index}
              xlGridSize={4}
              noTruncateDesc
              icon={EARN_OPTIONS_SURVEY_ICONS[id]}
              {...buildLoveBrandCardProps(
                LAD_SURVEY_IDS.EARN_OPTIONS,
                {
                  userSurveys,
                  userSurveysLikesCount,
                  id
                },
                onLikeUnlikeBrand
              )}
              {...question}
            />
          )
        )

      default:
        return null
    }
  }

  return (
    <PageContainer title={title}>
      <DisplayAlerts
        isDashboardSetupReady={isDashboardSetupReady}
        tokenStatus={tokenProfile.status}
        subscribedPackage={billingProfile.subscribedPackage}
        t={t}
      />
      <CardGrid>
        <Grid item xs={12}>
          <BaseCard>
            <PanelHeading title={title}>
              <TabContent
                selectedTab={fromExtensionsTypeToTabId(selectedTab)}
                onChangeTab={onChangeTab}
                menuItems={tabMenuItems(
                  thirdPartyToolsSurvey?.title,
                  earnOptionsSurvey?.title
                )}
                t={t}
                rootProps={{ mt: 1 }}
                content={buildContent()}
              />
            </PanelHeading>
          </BaseCard>
        </Grid>
        <CardListGrid>{buildPartnersList()}</CardListGrid>
      </CardGrid>
    </PageContainer>
  )
}

export default memo(Extensions)

const tabMenuItems = (thirdPartyToolsTitle, earnOptionsTitle) => {
  return [
    {
      value: EXTENSIONS_TAB_TYPE.THIRD_PARTY_TOOLS,
      label: thirdPartyToolsTitle,
      noTranslation: true,
      icon: faPlug
    },
    {
      value: EXTENSIONS_TAB_TYPE.EARN_OPTIONS,
      label: earnOptionsTitle,
      noTranslation: true,
      icon: faCoins
    }
  ]
}
